import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SIZE_UNITS } from '@emma-helpers/emma-validators.helper';
import { isFileSizeValid } from '@emma-helpers/file.helper';
import { get, noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';
import { getFileSizeError } from './emma-input-file.i18n';

const EMMA_INPUT_FILE_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputFileContentComponent),
  multi: true,
};

const EMMA_INPUT_FILE_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputFileContentComponent),
  multi: true,
};

@Component({
    selector: 'emma-input-file-content',
    templateUrl: './emma-input-file-content.component.html',
    providers: [
        EMMA_INPUT_FILE_CONTROL_VALUE_ACCESSOR,
        EMMA_INPUT_FILE_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAInputFileContentComponent,
        },
    ],
    standalone: false
})
export class EMMAInputFileContentComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  @Input() fileErrorText = $localize`Error cargando fichero`;
  @Input() override placeholder = $localize`Selecciona un fichero`;
  @Input() placeholderChange = $localize`Selecciona otro fichero`;
  @Input() fileLoadingText = $localize`Cargando...`;
  @Input() file?: File;

  @Input() previewUrl = '';
  @Input() clearable = true;
  @Input() maxSize = Infinity;
  @Input() maxSizeUnit = SIZE_UNITS.MB;
  @Input() minSize = 0;
  @Input() minSizeUnit = SIZE_UNITS.KB;
  @Input() fileId = `emma-input-file-content__${Math.random()}`;
  @Input() inputClass = '';
  _inputClass: Array<string> = [];

  filename = '';
  @Output() filenameChange = new EventEmitter<string>();

  @Input() isImage = false;
  @Input() accept = '*';
  @Input() contentValidator?: (content: string) => boolean;
  @Output() fileChange = new EventEmitter<File | undefined>();
  @Output() override $change = new EventEmitter<string>();
  @Output() $changeFile = new EventEmitter<File | undefined>();
  @Output() override $blur = new EventEmitter<string>();

  hasError = false;
  size = 0;

  // Form
  @Input()
  get value(): string {
    return this._innerValue;
  }
  set value(v: string) {
    if (v !== this._innerValue) {
      this._innerValue = v;
      this.onChangeCallback(this._innerValue);
      this.$change.emit(this._innerValue);
    }
  }
  private _innerValue = '';
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string) => void = noop;

  override ngOnInit(): void {
    this.updateClasses();
  }
  override ngOnChanges(changes: SimpleChanges): void {
    this.updateClasses();
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
    this.$blur.emit(this.value);
  }
  onClear() {
    this.file = undefined;
    this.size = 0;
    this.filename = '';
    this.value = '';
    this.hasError = false;
    this.fileChange.emit(undefined);
    this.filenameChange.emit('');
    this.updateClasses();
  }

  onChange(event: Event): void {
    // Clear
    this.value = '';
    this.hasError = false;
    this.fileErrorText = '';
    // Parse
    const target: any = event.target;
    if (target) {
      const file: File = target.files[0];
      this.file = file;
      this.fileChange.emit(this.file);
      if (file) {
        this.filename = target.files?.[0]?.name || target.value;
        this.filenameChange.emit(this.filename);
        this.size = file.size;
        if (this.isAllowedSize(file.size)) {
          this.readFile(file);
        } else {
          this.hasError = true;
          this.fileErrorText = getFileSizeError(
            file.size,
            this.minSize ? this.minSize * this.minSizeUnit : 0,
            this.maxSize < Infinity ? this.maxSize * this.maxSizeUnit : Infinity
          );
        }
      }
    }
    this.updateClasses();
  }

  private readFile(file: File): void {
    this.loading = true;
    const reader = new FileReader();
    reader.onload = (ev) => {
      this.loading = false;
      const content = get(ev, 'target.result', '') as string;
      if (!this.contentValidator || this.contentValidator(content)) {
        this.value = content;
      } else {
        this.value = '';
        this.hasError = true;
        this.fileErrorText = $localize`El fichero no tiene un formato válido.`;
      }
      this.updateClasses();
    };
    reader.onerror = (ev) => {
      this.loading = false;
      this.value = '';
      this.hasError = true;
      this.fileErrorText = $localize`Error de lectura del fichero.`;
      this.updateClasses();
    };
    // Exec reader
    if (this.isImage) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsText(file, 'UTF-8');
    }
  }

  private isAllowedSize(size: number) {
    return isFileSizeValid({
      maxSize: this.maxSize,
      minSize: this.minSize,
      maxSizeUnit: this.maxSizeUnit,
      minSizeUnit: this.minSizeUnit,
    })(size);
  }

  override updateClasses(): void {
    const containerClasses = ['emma-input-file-content', this.containerClass];
    this._containerClass = containerClasses;

    const inputClasses = [
      'emma-input-file-content__label',
      'm-input--solid',
      'emma-input__input',
      'form-control',
      'm-input',
      this.inputClass,
    ];
    if (this.hasError) {
      inputClasses.push('m-input--ko');
    } else if (this._innerValue) {
      inputClasses.push('m-input--ok');
    }
    this._inputClass = inputClasses;
  }

  // BEGIN From ControlValueAccessor interface
  writeValue(value: string) {
    if (value !== this._innerValue) {
      this._innerValue = value;
      this.hasError = false;
    }
    this.updateClasses();
  }
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }
  // END From ControlValueAccessor interface

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };
}
